import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Snackbar,
  Grid,
  Stack,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Dashboard as OverviewIcon,
  Description as DocumentIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEvents as IncentiveIcon,
  Timeline as TimelineIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  Timeline as ActivityIcon,
  CurrencyRupee as CurrencyRupeeIcon,
} from '@mui/icons-material';
import { useBookingDetails } from '../../contexts/BookingDetailsContext';
import BookingSummaryCard from './booking_details/BookingSummaryCard';
import BookingTabContent from './booking_details/BookingTabContent';
import BookingSidebar from './booking_details/BookingSidebar';
import { bookingTokens, STATUS_CFG } from './booking_details/bookingDetailsTheme';

const BookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Context
  const {
    fetchBookingDetails,
    currentBooking,
    clearBookingDetails,
    bookingLoading,
    error,
    updateBookingStatus
  } = useBookingDetails();

  // Tab state
  const [selectedTab, setSelectedTab] = useState(0);

  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editFinanceOpen, setEditFinanceOpen] = useState(false);
  const [addProjectUnitDialogOpen, setAddProjectUnitDialogOpen] = useState(false);

  const booking = currentBooking;
  const loading = bookingLoading;

  // Effect must always run
  useEffect(() => {
    if (id && fetchBookingDetails) {
      fetchBookingDetails(id);
    }
    return () => {
      if (clearBookingDetails) clearBookingDetails();
    };
  }, [id, fetchBookingDetails, clearBookingDetails]);

  const handleStatusUpdate = async (booking, newStatus) => {
    if (!updateBookingStatus) return;

    setActionLoading(true);
    try {
      await updateBookingStatus(booking.id, newStatus);

      setSnackbar({
        open: true,
        message: `Booking status updated to ${newStatus}`,
        severity: 'success'
      });

      if (id && fetchBookingDetails) {
        await fetchBookingDetails(id);
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update booking status',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleActionComplete = () => {
    if (id && fetchBookingDetails) {
      fetchBookingDetails(id);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Tab definitions with icons, colors, and badges (similar to lead details)
  const tabDefinitions = [
    {
      value: 0,
      label: 'Overview',
      icon: <OverviewIcon fontSize="small" />,
      color: '#2196F3', // Blue
      count: null
    },
    {
      value: 1,
      label: 'Booking Payments',
      icon: <PaymentIcon fontSize="small" />,
      color: '#4CAF50', // Green
      count: null
    },
    // {
    //   value: 2,
    //   label: 'Commission Raised',
    //   icon: <PaymentIcon fontSize="small" />,
    //   color: '#9C27B0', // Purple
    //   count: null
    // },
    {
      value: 2,
      label: 'Commission Payment',
      icon: <PaymentIcon fontSize="small" />,
      color: '#FF9800', // Orange
      count: null
    },
    {
      value: 3,
      label: 'Agent Incentive',
      icon: <IncentiveIcon fontSize="small" />,
      color: '#E91E63', // Pink
      count: null
    },
    {
      value: 4,
      label: 'Documents',
      icon: <DocumentIcon fontSize="small" />,
      color: '#607D8B', // Blue Grey
      count: null
    },
    {
      value: 5,
      label: 'Timeline',
      icon: <ActivityIcon fontSize="small" />,
      color: '#795548', // Brown
      count: null
    }
  ];

  if (loading) {
    return (
      <Box sx={{ bgcolor: bookingTokens.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: bookingTokens.green }} />
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Booking not found'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/bookings')}
        >
          Back to Bookings
        </Button>
      </Container>
    );
  }

  const statusCfg = STATUS_CFG[booking.status] || STATUS_CFG.draft;

  return (
    <Box sx={{ fontFamily: "'DM Sans','Plus Jakarta Sans',sans-serif", bgcolor: bookingTokens.bg, minHeight: '100vh' }}>
      {/* Top nav - dark bar: Bookings › ID | status | Edit | Update Finance */}
      {/* Main layout: sidebar + content */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { md: 'calc(100vh - 54px)' }, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box
          sx={{
            width: { xs: '100%', md: 272 },
            minHeight: { xs: 'auto', md: '100%' },
            flexShrink: 0,
            bgcolor: bookingTokens.white,
            borderRight: { md: `1.5px solid ${bookingTokens.border}` },
            borderBottom: { xs: `1.5px solid ${bookingTokens.border}`, md: 'none' },
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <BookingSidebar
            booking={booking}
            onActionComplete={() => fetchBookingDetails(id)}
            addProjectUnitDialogOpen={addProjectUnitDialogOpen}
            onOpenAddUnitDialog={() => setAddProjectUnitDialogOpen(true)}
            onCloseAddUnitDialog={() => setAddProjectUnitDialogOpen(false)}
          />
        </Box>

        {/* Main content */}
        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Meta bar + Customer strip */}
          <BookingSummaryCard
            booking={booking}
            onStatusUpdate={handleStatusUpdate}
            actionLoading={actionLoading}
            onActionComplete={handleActionComplete}
            fetchBookingDetails={fetchBookingDetails}
            editFinanceOpen={editFinanceOpen}
            onEditFinanceOpen={() => setEditFinanceOpen(true)}
            onEditFinanceClose={() => setEditFinanceOpen(false)}
            compactLayout
          />

          {/* Tabs - reference style with green underline */}
          <Box sx={{ bgcolor: bookingTokens.white, borderBottom: `1.5px solid ${bookingTokens.border}`, px: 3, flexShrink: 0, overflowX: 'auto' }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 48,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: 13,
                  minWidth: 80,
                  color: bookingTokens.subtle,
                  transition: 'all 150ms',
                  '&.Mui-selected': { color: bookingTokens.green, fontWeight: 700 },
                  '& .MuiTab-iconWrapper': { marginRight: 0.75, marginBottom: 0 },
                },
                '& .MuiTabs-indicator': {
                  height: 2.5,
                  backgroundColor: bookingTokens.green,
                  borderRadius: '2.5px 2.5px 0 0',
                },
              }}
            >
              <Tab icon={<OverviewIcon fontSize="small" />} iconPosition="start" label="Overview" />
              <Tab icon={<ReceiptIcon fontSize="small" />} iconPosition="start" label="Booking Payments" />
              <Tab icon={<CheckCircleIcon fontSize="small" />} iconPosition="start" label="Commission Received" />
              <Tab icon={<IncentiveIcon fontSize="small" />} iconPosition="start" label="Agent Incentive" />
              <Tab icon={<DocumentIcon fontSize="small" />} iconPosition="start" label="Documents" />
              <Tab icon={<TimelineIcon fontSize="small" />} iconPosition="start" label="Timeline" />
            </Tabs>
          </Box>

          {/* Tab content */}
          <Box sx={{ flex: 1, px: 3, py: 2.75 }}>
            <BookingTabContent
              booking={booking}
              selectedTab={selectedTab}
              onActionComplete={handleActionComplete}
              onOpenAddUnitDialog={() => setAddProjectUnitDialogOpen(true)}
            />
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingDetails;
